// src/landing/DemoLeadForm.jsx
// ─── Formulaire de démonstration (§22-39) ───────────────────────────────────
// Utilisé par le hero de la landing ET par la page /demo. Un seul composant,
// un seul endpoint (/api/public/demo-lead), une seule table CRM (pro_leads).
//
// §30-33 — Attribution. Les paramètres d'URL sont lus au montage et joints au
// lead : utm_* pour les campagnes, ref pour un apporteur. Ils sont conservés
// en sessionStorage : un visiteur arrivé par une publicité, qui navigue trois
// pages puis remplit le formulaire, garde sa provenance.
//
// §38-39 — Tracking. Quatre évènements sont poussés vers dataLayer (GA) et
// fbq (Meta) s'ils existent. JAMAIS le nom, l'email ou le téléphone : seuls
// des drapeaux et la source.

import { useState, useEffect, useRef } from "react";

const NEEDS = [
  ["gain_temps", "Gagner du temps"], ["analyse_vehicules", "Analyser mes véhicules"],
  ["opportunites", "Trouver des opportunités"], ["structurer_dossiers", "Structurer mes dossiers"],
  ["piloter_equipe", "Piloter mon équipe"], ["autre", "Autre"],
];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function captureAttribution() {
  if (typeof window === "undefined") return {};
  try {
    const stored = JSON.parse(sessionStorage.getItem("viq_attribution") || "{}");
    const q = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const k of UTM_KEYS) { const v = q.get(k); if (v) fresh[k] = v.slice(0, 120); }
    const ref = q.get("ref") || q.get("lead");
    if (ref) fresh.ref = ref.toUpperCase().slice(0, 40);
    const merged = { ...stored, ...fresh };
    if (!merged.landing_page) merged.landing_page = window.location.pathname + window.location.search;
    if (!merged.referrer && document.referrer) merged.referrer = document.referrer.slice(0, 300);
    sessionStorage.setItem("viq_attribution", JSON.stringify(merged));
    return merged;
  } catch (e) { return {}; }
}

function track(event, data = {}) {
  try {
    if (window.dataLayer) window.dataLayer.push({ event, ...data });
    if (typeof window.fbq === "function") {
      if (event === "lead_form_success") window.fbq("track", "Lead", data);
      else window.fbq("trackCustom", event, data);
    }
  } catch (e) {}
}

export default function DemoLeadForm({ apiUrl, onPrivacy, onDiscover, variant = "hero" }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", vehicle_volume: "", primary_need: "", website: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const started = useRef(false);
  const attribution = useRef({});

  useEffect(() => {
    attribution.current = captureAttribution();
    track("lead_form_view", { variant, source: attribution.current.utm_source || "direct" });
  }, [variant]);

  const set = (k) => (e) => {
    if (!started.current) { started.current = true; track("lead_form_start", { variant }); }
    setF(x => ({ ...x, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setError(null); setState("sending");
    track("lead_form_submit", { variant, has_phone: !!f.phone, need: f.primary_need || "none" });
    try {
      const r = await fetch(`${apiUrl}/api/public/demo-lead`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, ...attribution.current, page: window.location.pathname }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success) {
        setState("done");
        track("lead_form_success", { variant, source: attribution.current.utm_source || "direct", campaign: attribution.current.utm_campaign || "" });
      } else { setError(j.error || "Votre demande n'a pas pu être envoyée."); setState("error"); }
    } catch (err) { setError("Connexion impossible. Réessayez dans un instant."); setState("error"); }
  };

  if (state === "done") {
    const first = f.name.trim().split(/\s+/)[0];
    return (
      <div className="lv2-form" role="status">
        <div className="lv2-form-done">
          <h3>Merci{first ? ` ${first}` : ""}.</h3>
          <p>Votre demande est bien enregistrée.</p>
          <p>Nous vous contacterons rapidement pour vous présenter VIQ.AI.</p>
          <button type="button" className="lv2-btn secondary md" onClick={onDiscover}>Découvrir VehicleIQ</button>
        </div>
      </div>
    );
  }

  return (
    <form className="lv2-form" onSubmit={submit} noValidate>
      <div className="lv2-form-title">Découvrez VIQ.AI sur votre activité.</div>
      <div className="lv2-form-sub">Laissez vos coordonnées et nous vous montrons comment VehicleIQ peut s'intégrer à votre activité VO.</div>

      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

      <label className="lv2-field"><span>Nom et prénom</span>
        <input required value={f.name} onChange={set("name")} autoComplete="name" placeholder="Jean Martin" /></label>
      <label className="lv2-field"><span>Entreprise</span>
        <input value={f.company} onChange={set("company")} autoComplete="organization" placeholder="Auto Prestige" /></label>
      <label className="lv2-field"><span>Email professionnel</span>
        <input required type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" placeholder="jean@autoprestige.fr" /></label>
      <div className="lv2-row">
        <label className="lv2-field"><span>Téléphone</span>
          <input type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" placeholder="06 12 34 56 78" /></label>
        <label className="lv2-field"><span>VO / mois</span>
          <input type="number" inputMode="numeric" min="0" value={f.vehicle_volume} onChange={set("vehicle_volume")} placeholder="20" /></label>
      </div>
      <label className="lv2-field"><span>Besoin principal</span>
        <select value={f.primary_need} onChange={set("primary_need")}>
          <option value="">Choisir…</option>
          {NEEDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>

      {error && <div className="lv2-form-error" role="alert">{error}</div>}

      <button type="submit" className="lv2-btn primary lg" disabled={state === "sending"} style={{ width: "100%" }}>
        {state === "sending" ? "Envoi…" : "Demander une démonstration"}
      </button>
      <div className="lv2-consent">
        En envoyant ce formulaire, vous acceptez d'être recontacté par VehicleIQ.{" "}
        <button type="button" onClick={() => onPrivacy && onPrivacy("privacy")}>Politique de confidentialité</button>
      </div>
    </form>
  );
}
